import { useMemo, useState } from "react"
import { Badge, Button, Spinner } from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import { useActivityLog, useActivityLogs } from "hooks/useActivityLog"
import { useListParams } from "hooks/useListParams"
import { formatReadableDate } from "utils/formatDate"
import ActivityLogView from "pages/SystemPages/ActivityLog/ActivityLogView"

const ACTION_STYLES = {
  create: { color: "success", icon: "mdi-plus-circle" },
  update: { color: "info", icon: "mdi-pencil-circle" },
  delete: { color: "danger", icon: "mdi-delete-circle" },
  restore: { color: "warning", icon: "mdi-restore" },
  login: { color: "primary", icon: "mdi-login" },
  logout: { color: "secondary", icon: "mdi-logout" },
  grant_access: { color: "success", icon: "mdi-account-key" },
  revoke_access: { color: "danger", icon: "mdi-account-off" },
  update_roles: { color: "info", icon: "mdi-shield-account" },
  reset_password: { color: "warning", icon: "mdi-lock-reset" },
  impersonate: { color: "dark", icon: "mdi-account-switch" },
}

const ActivityList = ({ stock }) => {
  const { params, updateParams } = useListParams({
    a_page: 1,
    a_per_page: 20,
  })
  const currentPage = Number(params?.a_page) || 1
  const perPage = Number(params?.a_per_page) || 20
  const [selectedId, setSelectedId] = useState(null)
  const [viewOpen, setViewOpen] = useState(false)

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      per_page: perPage,
      vehicle_id: stock?.id,
    }),
    [currentPage, perPage, stock?.id]
  )

  const enabled = Boolean(stock?.id)
  const { data, isLoading, isRefetching, refetch } = useActivityLogs(
    queryParams,
    enabled
  )
  const { data: detailData, isLoading: isDetailLoading } = useActivityLog(
    selectedId,
    !!selectedId
  )

  const paginationInfo = data?.data || {}
  const logs = data?.data?.data || []

  const setPage = page => updateParams({ a_page: page })

  const columns = useMemo(
    () => [
      {
        header: "ID",
        cell: ({ row }) => (
          <span className="font-monospace fw-semibold">#{row.original.id}</span>
        ),
      },
      {
        header: "Module",
        cell: ({ row }) => (
          <Badge color="light" className="text-dark text-uppercase">
            {row.original.module}
          </Badge>
        ),
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const action = row.original.action
          const config = ACTION_STYLES[action] || {
            color: "secondary",
            icon: "mdi-history",
          }
          return (
            <Badge
              color={config.color}
              className="d-flex align-items-center gap-1 w-fit"
            >
              <i className={`mdi ${config.icon}`} />
              <span>{action}</span>
            </Badge>
          )
        },
      },
      {
        header: "Subject",
        cell: ({ row }) => (
          <div>
            <div className="fw-semibold">
              {row.original.subject_label || "N/A"}
            </div>
            <div className="small text-muted">
              {row.original.subject_type?.split("\\").pop() || "Unknown"}
              {row.original.subject_id ? ` #${row.original.subject_id}` : ""}
            </div>
          </div>
        ),
      },
      {
        header: "User",
        cell: ({ row }) => (
          <div className="small">
            {row.original.causer_user_display ||
              row.original.causer_user_snapshot?.label ||
              row.original.user?.name ||
              row.original.user?.username ||
              "System"}
          </div>
        ),
      },
      {
        header: "Time",
        cell: ({ row }) => (
          <small className="text-muted">
            {formatReadableDate(row.original.created_at)}
          </small>
        ),
      },
      {
        header: "View",
        cell: ({ row }) => (
          <Button
            color="primary"
            size="sm"
            onClick={() => {
              setSelectedId(row.original.id)
              setViewOpen(true)
            }}
          >
            <i className="mdi mdi-eye-outline" />
          </Button>
        ),
      },
    ],
    []
  )

  if (!enabled) {
    return <div className="text-muted small">Vehicle not loaded.</div>
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0 fw-semibold">Activity</h5>
          <div className="small text-muted">
            Total: {paginationInfo?.total ?? 0}
          </div>
        </div>
        <Button
          color="light"
          size="sm"
          onClick={() => refetch()}
          disabled={isRefetching}
        >
          <i
            className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""} me-1`}
          />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner size="sm" color="primary" />
          <p className="text-muted small mt-2">Loading activity...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="mdi mdi-inbox-outline display-4 opacity-50" />
          <p className="mt-2">No activity found for this vehicle</p>
        </div>
      ) : (
        <TableContainer
          columns={columns}
          data={logs}
          isPagination={!!paginationInfo}
          paginationInfo={paginationInfo}
          onPageChange={setPage}
          loading={isLoading || isRefetching}
        />
      )}

      <ActivityLogView
        isOpen={viewOpen}
        toggle={() => {
          setViewOpen(false)
          setSelectedId(null)
        }}
        activityLog={detailData?.data}
        isLoading={isDetailLoading}
      />
    </>
  )
}

export default ActivityList
